import * as _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import { useAsyncValue } from '../../../../../hooks';
import { patch } from '../../../../../redux/api';

const getEndpoint = (orgId: number) => `/api/organizations/${orgId}/settings/projects/notifications`;

const usePropagateNotifications = (orgId: number, onHide: () => void) => {
  const addToast = useAddToast();
  const [isPropagateEmailNotificationsEnabled, setIsPropagateEmailNotificationsEnabled] = useState<boolean>(false);
  const [isPropagateSlackNotificationsEnabled, setIsPropagateSlackNotificationsEnabled] = useState<boolean>(false);
  const [isPropagateApiWebhookNotificationsEnabled, setIsPropagateApiWebhookNotificationsEnabled] =
    useState<boolean>(false);
  const asyncValue = useAsyncValue<void>();
  const propagatePayload = useMemo(
    () =>
      _.compact([
        isPropagateEmailNotificationsEnabled ? 'notificationDefaultEmailScanUsers' : null,
        isPropagateSlackNotificationsEnabled ? 'notificationDefaultSlackScan' : null,
        isPropagateApiWebhookNotificationsEnabled ? 'notificationDefaultApiWebhookScan' : null,
      ]),
    [
      isPropagateEmailNotificationsEnabled,
      isPropagateSlackNotificationsEnabled,
      isPropagateApiWebhookNotificationsEnabled,
    ],
  );
  const onPropagateSettings = useCallback(async () => {
    try {
      asyncValue.setPending();
      await patch(getEndpoint(orgId), propagatePayload);
      asyncValue.setResolve();
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHide();
    } catch (error: unknown) {
      asyncValue.setReject(['Failed to apply settings to all projects.']);
    }
  }, [addToast, asyncValue, onHide, orgId, propagatePayload]);

  return {
    isPropagateEmailNotificationsEnabled,
    isPropagateSlackNotificationsEnabled,
    isPropagateApiWebhookNotificationsEnabled,
    togglePropagateEmailNotificationsEnabled: () =>
      setIsPropagateEmailNotificationsEnabled(
        (previousIsPropagateEmailNotificationsEnabled) => !previousIsPropagateEmailNotificationsEnabled,
      ),
    togglePropagateSlackNotificationsEnabled: () =>
      setIsPropagateSlackNotificationsEnabled(
        (previousIsPropagateSlackNotificationsEnabled) => !previousIsPropagateSlackNotificationsEnabled,
      ),
    togglePropagateApiWebhookNotificationsEnabled: () =>
      setIsPropagateApiWebhookNotificationsEnabled(
        (previousIsPropagateApiWebhookNotificationsEnabled) => !previousIsPropagateApiWebhookNotificationsEnabled,
      ),
    onPropagateSettings,
    isLoading: asyncValue.isLoading,
    errors: asyncValue.errors,
    isSaveButtonDisabled: _.isEmpty(propagatePayload),
  };
};

export default usePropagateNotifications;
